import { useCallback, useMemo, useRef, useState } from "react";
import {
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	Alert,
	AlertIcon,
	Text,
	Spinner,
} from "@chakra-ui/react";
import { FiUpload } from "react-icons/fi";

import { Button } from "newui/components/Button";
import type { QuoteResourceType } from "../../api/procurer-quotes-api";
import type { QuoteImportApi } from "../../api";
import { resolveQuoteImportApi } from "../../api";
import type {
	ImportedCellValue,
	ImportedQuoteRow,
	ImportedQuoteTable,
} from "../../types/imported-quote-table.type";
import { AiSpreadsheetImportPreview } from "./AiSpreadsheetImportPreview";
import {
	useAnalyzeSpreadsheet,
	useApplyQuoteImport,
} from "./hooks/useSpreadsheetImport";

export interface AiSpreadsheetImportProps {
	isOpen: boolean;
	/** Resource id — an `rfxId` for `rfx`, a `rfx_template_id` for `rfx-template`. */
	resourceId: number;
	/** Selects which import endpoint the modal targets (AC2). Defaults to `rfx`. */
	resourceType?: QuoteResourceType;
	/**
	 * Optional import-api override. When omitted, the api is resolved from
	 * `resourceType`/`resourceId`. Lets tasks 007/010 inject a bespoke module.
	 */
	importApi?: QuoteImportApi;
	onClose: () => void;
	onImportComplete: () => void;
}

const ALLOWED_MIME_TYPES = [
	"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
	"application/vnd.ms-excel",
];

const extractErrorMessage = (error: unknown, fallback: string): string => {
	const response = (error as { response?: { data?: { message?: string } } })
		?.response;
	if (typeof response?.data?.message === "string" && response.data.message) {
		return response.data.message;
	}
	return fallback;
};

const nextSortOrder = (items: { sort_order: number }[]): number =>
	items.reduce((max, item) => Math.max(max, item.sort_order), -1) + 1;

const AiSpreadsheetImport = ({
	resourceId,
	resourceType = "rfx",
	importApi,
	isOpen,
	onClose,
	onImportComplete,
}: AiSpreadsheetImportProps) => {
	const api = useMemo(
		() => importApi ?? resolveQuoteImportApi(resourceType, resourceId),
		[importApi, resourceType, resourceId]
	);

	const [tables, setTables] = useState<ImportedQuoteTable[] | null>(null);
	const [workbookWarnings, setWorkbookWarnings] = useState<string[]>([]);
	const [activeTableIndex, setActiveTableIndex] = useState(0);
	const [fileError, setFileError] = useState("");
	const fileInputRef = useRef<HTMLInputElement>(null);

	const analyzeMutation = useAnalyzeSpreadsheet(api);
	const applyMutation = useApplyQuoteImport(api);

	const errorMessage =
		fileError ||
		(analyzeMutation.isError
			? extractErrorMessage(
					analyzeMutation.error,
					"Could not parse this spreadsheet. Please check the file format and try again."
			  )
			: "") ||
		(applyMutation.isError
			? extractErrorMessage(
					applyMutation.error,
					"Failed to save imported tables. Please try again."
			  )
			: "");

	const updateActiveTable = useCallback(
		(mutator: (table: ImportedQuoteTable) => ImportedQuoteTable) => {
			setTables((prev) => {
				if (!prev) {
					return prev;
				}
				return prev.map((table, idx) =>
					idx === activeTableIndex ? mutator(table) : table
				);
			});
		},
		[activeTableIndex]
	);

	const handleFileSelect = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const selectedFile = e.target.files?.[0];
			if (!selectedFile) {
				return;
			}
			if (!ALLOWED_MIME_TYPES.includes(selectedFile.type)) {
				setFileError("Please select an Excel file (.xlsx or .xls)");
				return;
			}

			setFileError("");
			setTables(null);
			setWorkbookWarnings([]);
			analyzeMutation.reset();
			analyzeMutation.mutate(selectedFile, {
				onSuccess: (result) => {
					setTables(result.tables);
					setWorkbookWarnings(result.warnings ?? []);
					setActiveTableIndex(0);
				},
			});
		},
		[analyzeMutation]
	);

	const handleTableNameChange = useCallback(
		(name: string) => {
			updateActiveTable((table) => ({ ...table, name }));
		},
		[updateActiveTable]
	);

	const handleColumnTypeChange = useCallback(
		(columnSortOrder: number, columnType: string) => {
			updateActiveTable((table) => ({
				...table,
				columns: table.columns.map((col) =>
					col.sort_order === columnSortOrder
						? {
								...col,
								column_type:
									(columnType ||
										null) as ImportedQuoteTable["columns"][number]["column_type"],
						  }
						: col
				),
			}));
		},
		[updateActiveTable]
	);

	const handleColumnNameChange = useCallback(
		(columnSortOrder: number, name: string) => {
			updateActiveTable((table) => ({
				...table,
				columns: table.columns.map((col) =>
					col.sort_order === columnSortOrder ? { ...col, name } : col
				),
			}));
		},
		[updateActiveTable]
	);

	const handleCellEdit = useCallback(
		(rowSortOrder: number, columnSortOrder: number, value: string) => {
			updateActiveTable((table) => ({
				...table,
				rows: table.rows.map((row) =>
					row.sort_order === rowSortOrder
						? {
								...row,
								cells: { ...row.cells, [columnSortOrder]: value },
						  }
						: row
				),
			}));
		},
		[updateActiveTable]
	);

	const handleAddRow = useCallback(() => {
		updateActiveTable((table) => {
			const emptyCells = table.columns.reduce<
				Record<number, ImportedCellValue>
			>((acc, col) => {
				acc[col.sort_order] = "";
				return acc;
			}, {});
			const newRow: ImportedQuoteRow = {
				sort_order: nextSortOrder(table.rows),
				cells: emptyCells,
			};
			return { ...table, rows: [...table.rows, newRow] };
		});
	}, [updateActiveTable]);

	const handleRemoveRow = useCallback(
		(rowSortOrder: number) => {
			updateActiveTable((table) => ({
				...table,
				rows: table.rows.filter((row) => row.sort_order !== rowSortOrder),
			}));
		},
		[updateActiveTable]
	);

	const handleRemoveTable = useCallback(() => {
		setTables((prev) => {
			if (!prev || prev.length <= 1) {
				return prev;
			}
			const updated = prev.filter((_, idx) => idx !== activeTableIndex);
			setActiveTableIndex((current) =>
				current >= updated.length ? updated.length - 1 : current
			);
			return updated;
		});
	}, [activeTableIndex]);

	const handleSave = useCallback(() => {
		if (!tables) {
			return;
		}
		applyMutation.reset();
		applyMutation.mutate(tables, {
			onSuccess: () => {
				onImportComplete();
				onClose();
			},
		});
	}, [tables, applyMutation, onImportComplete, onClose]);

	const handleReset = useCallback(() => {
		setTables(null);
		setWorkbookWarnings([]);
		setFileError("");
		setActiveTableIndex(0);
		analyzeMutation.reset();
		applyMutation.reset();
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	}, [analyzeMutation, applyMutation]);

	const isAnalysing = analyzeMutation.isPending;
	const isSaving = applyMutation.isPending;
	const hasPreview = !!tables && tables.length > 0;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="6xl">
			<ModalOverlay />
			<ModalContent data-testid="ai-import-modal" maxW="min(1100px, 95vw)">
				<ModalHeader>Import from Spreadsheet</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					{!hasPreview && !isAnalysing && (
						<div
							className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 p-8"
							data-testid="file-upload-area"
						>
							<FiUpload className="mb-3 text-gray-400" size={32} />
							<Text className="mb-2 text-sm font-medium text-gray-700">
								Select an Excel file to import
							</Text>
							<Text className="mb-4 text-xs text-gray-500">
								Supports .xlsx and .xls files. Tables will be automatically detected.
							</Text>
							<input
								ref={fileInputRef}
								type="file"
								accept=".xlsx,.xls"
								onChange={handleFileSelect}
								className="hidden"
								data-testid="ai-file-input"
							/>
							<Button
								size="sm"
								btntype="primary"
								onClick={() => fileInputRef.current?.click()}
								data-testid="ai-select-file-btn"
							>
								Choose File
							</Button>
						</div>
					)}

					{isAnalysing && (
						<div
							className="flex flex-col items-center justify-center py-8"
							data-testid="analysing-spinner"
						>
							<Spinner size="lg" className="mb-3" />
							<Text className="text-sm text-gray-600">
								Analysing spreadsheet structure...
							</Text>
						</div>
					)}

					{errorMessage && (
						<Alert
							status="error"
							className="mt-3 rounded-md"
							data-testid="ai-import-error"
						>
							<AlertIcon />
							<Text className="text-sm">{errorMessage}</Text>
						</Alert>
					)}

					{hasPreview && workbookWarnings.length > 0 && (
						<Alert
							status="warning"
							className="mb-3 rounded-md"
							data-testid="workbook-warning"
						>
							<AlertIcon />
							<div className="text-sm">
								{workbookWarnings.map((warning) => (
									<Text key={warning}>{warning}</Text>
								))}
							</div>
						</Alert>
					)}

					{hasPreview && tables && (
						<>
							<div className="mb-3 flex justify-end">
								<Button
									size="xs"
									btntype="secondary"
									onClick={handleReset}
									data-testid="ai-reset-btn"
								>
									Choose Different File
								</Button>
							</div>
							<AiSpreadsheetImportPreview
								tables={tables}
								activeTableIndex={activeTableIndex}
								onSelectTable={setActiveTableIndex}
								onTableNameChange={handleTableNameChange}
								onRemoveTable={handleRemoveTable}
								onColumnTypeChange={handleColumnTypeChange}
								onColumnNameChange={handleColumnNameChange}
								onCellEdit={handleCellEdit}
								onAddRow={handleAddRow}
								onRemoveRow={handleRemoveRow}
							/>
						</>
					)}
				</ModalBody>
				<ModalFooter>
					<Button
						btntype="secondary"
						onClick={onClose}
						className="mr-3"
						data-testid="ai-cancel-btn"
					>
						Cancel
					</Button>
					{hasPreview && (
						<Button
							btntype="primary"
							onClick={handleSave}
							disabled={isSaving}
							data-testid="ai-save-btn"
						>
							{isSaving ? "Importing..." : "Import Tables"}
						</Button>
					)}
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

export { AiSpreadsheetImport };
